import { useEffect, useState } from "react";
import { Button, Card, Pagination, Table } from "react-bootstrap";
import { BsSortAlphaDownAlt, BsSortAlphaUpAlt } from "react-icons/bs";
import { FaArrowUp } from "react-icons/fa";

import { generateRandomId } from "../utils/helperFunctions";

export default function CustomTable(props) {
  const { headers = [], rows = [], colCanSort = [], paginationSize, loading = false } = props;
  const [sortByCol, setSortByCol] = useState(-1);
  const [sortByDir, setSortByDir] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [isMobile, setIsMobile] = useState(false);
  const [touchStart, setTouchStart] = useState(0);

  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const mobileSortOptions = headers.map((header, index) => ({ header, index })).filter((_, i) => colCanSort[i]);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Add missing function
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  // Swipe handling
  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    if (!touchStart) return;

    const touchEnd = e.touches[0].clientX;
    const diff = touchStart - touchEnd;

    if (Math.abs(diff) > 50) {
      // Minimum swipe distance
      if (diff > 0) {
        handlePageChange(Math.min(currentPage + 1, totalPages));
      } else {
        handlePageChange(Math.max(currentPage - 1, 1));
      }
    }
    setTouchStart(0);
  };

  // Sorting logic
  const handleSort = (index) => {
    if (colCanSort[index]) {
      setSortByCol((prev) => (prev === index ? prev : index));
      setSortByDir((prev) => (prev === 1 ? -1 : 1));
    }
  };

  // Data processing
  const sortedRows = [...rows].sort((a, b) => {
    const aVal = a[sortByCol]?.value || "";
    const bVal = b[sortByCol]?.value || "";
    return colCanSort[sortByCol] ? sortByDir * aVal.localeCompare(bVal) : 0;
  });

  const totalPages = paginationSize ? Math.ceil(sortedRows.length / paginationSize) : 1;
  const paginatedRows = paginationSize
    ? sortedRows.slice((currentPage - 1) * paginationSize, currentPage * paginationSize)
    : sortedRows;

  // Scroll to top
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div>
      {/* Mobile Floating Action Button */}
      {/* {isMobile && (
        <Button
          variant="primary"
          className="d-md-none fixed-bottom end-0 mb-3 me-3 rounded-circle shadow-lg"
          style={{ width: "50px", height: "50px" }}
          onClick={scrollToTop}
        >
          <FaArrowUp />
        </Button>
      )} */}

      {/* Table Container */}
      <div
        className="table-responsive"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        style={{ minHeight: "400px" }}
      >
        {/* Loading Skeletons */}
        {loading ? (
          [...Array(5)].map((_, i) => (
            <Card key={i} className="mb-2">
              <Card.Body className="d-flex flex-column gap-2">
                {headers.map((_, hi) => (
                  <div
                    key={hi}
                    className="skeleton-loader"
                    style={{
                      height: "20px",
                      width: `${Math.random() * 40 + 60}%`,
                      borderRadius: "4px",
                    }}
                  />
                ))}
              </Card.Body>
            </Card>
          ))
        ) : /* Mobile Card View */

        isMobile ? (
          <div className="mb-3">
            {/* Mobile Sorting Controls */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="d-flex align-items-center gap-2">
                <Button variant="outline-primary" size="sm" onClick={() => setShowSortDropdown(!showSortDropdown)}>
                  Sort {sortByCol > -1 && `(${headers[sortByCol]})`}
                </Button>
                {showSortDropdown && (
                  <div className="bg-white shadow rounded p-2">
                    {mobileSortOptions.map(({ header, index }) => (
                      <div
                        key={index}
                        className="clickable p-2"
                        onClick={() => {
                          handleSort(index);
                          setShowSortDropdown(false);
                        }}
                      >
                        {header}
                        {sortByCol === index && (
                          <span className="ms-2">{sortByDir === 1 ? <BsSortAlphaUpAlt /> : <BsSortAlphaDownAlt />}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="text-muted small">{sortedRows.length} items</div>
            </div>

            {/* Cards */}
            {paginatedRows.map((row, ri) => (
              <Card key={generateRandomId()} className="mb-3 shadow-sm" onClick={row[0]?.onClick}>
                <Card.Body>
                  {row.map((col, ci) => (
                    <div key={ci} className="d-flex justify-content-between mb-2" onClick={col?.onClick}>
                      <span className="text-muted fw-bold">
                        {headers[ci]}:
                        {sortByCol === ci && (
                          <span className="ms-1">{sortByDir === 1 ? <BsSortAlphaUpAlt /> : <BsSortAlphaDownAlt />}</span>
                        )}
                      </span>
                      <span className="text-truncate" style={{ maxWidth: "60%" }}>
                        {col?.value || "N/A"}
                      </span>
                    </div>
                  ))}
                </Card.Body>
              </Card>
            ))}
          </div>
        ) : (
          /* Desktop Table View */
          <Table responsive borderless hover className="align-middle mb-0 custom-table">
            <thead>
              <tr>
                {headers.map((hd, hi) => (
                  <th
                    key={generateRandomId()}
                    onClick={() => handleSort(hi)}
                    style={{
                      backgroundColor: "#6EC3C4",
                      color: "white",
                      padding: "0.75rem",
                      minWidth: "120px",
                      cursor: colCanSort[hi] ? "pointer" : "default",
                    }}
                  >
                    <div className="d-flex align-items-center gap-1">
                      <span>{hd}</span>
                      {colCanSort[hi] &&
                        sortByCol === hi &&
                        (sortByDir === 1 ? <BsSortAlphaUpAlt /> : <BsSortAlphaDownAlt />)}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedRows.map((row, ri) => (
                <tr key={generateRandomId()} className="shadow-sm" onClick={row[0]?.onClick}>
                  {row.map((col, ci) => (
                    <td
                      key={generateRandomId()}
                      style={{
                        padding: "0.75rem",
                        backgroundColor: ci === 1 ? "#FF7F7F22" : "transparent",
                        verticalAlign: "middle",
                      }}
                    >
                      <div className="text-truncate" style={{ maxWidth: "200px" }} onClick={col?.onClick}>
                        {col?.value || "N/A"}
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>

      {/* Pagination */}
      {!loading && paginationSize && totalPages > 1 && (
        <div className="mt-4">
          <Pagination className="flex-wrap justify-content-center">
            <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
            <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />

            {[...Array(totalPages)].map((_, i) => (
              <Pagination.Item
                key={i + 1}
                active={i + 1 === currentPage}
                onClick={() => handlePageChange(i + 1)}
                className="mx-1"
                style={{
                  minWidth: "40px",
                  textAlign: "center",
                  borderRadius: "8px",
                }}
              >
                {i + 1}
              </Pagination.Item>
            ))}

            <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
            <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
          </Pagination>
        </div>
      )}

      {/* Custom Styles */}
      <style>{`
  @media (max-width: 768px) {
    .table-responsive {
      border: none;
      padding: 0;
    }

    .custom-table tr {
      display: none !important;
    }
  }

  .skeleton-loader {
    background: linear-gradient(
      90deg,
      rgba(240, 240, 240, 0.9) 25%,
      rgba(230, 230, 230, 0.9) 50%,
      rgba(240, 240, 240, 0.9) 75%
    );
    background-size: 400% 400%;
    animation: loading 1.5s ease-in-out infinite;
  }

  @keyframes loading {
    0% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }
`}</style>
    </div>
  );
}
